import { buildPointPaySalePath } from '@am3nd-org/pointcraft-types';

// ----------------------------------------------------------------------

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
  SYSTEM: '/system',
  PUBLIC: '/ps',
  COMPANION: '/cd',
  OPERATIONAL_APPS: '/operational-apps',
  ONLINE_PAYMENT: '/online-payment',
};

// ----------------------------------------------------------------------

export const paths = {
  minimalUI: '/dashboard', // TODO: Find every instance of this and remove it
  stripePayoutCardHandoff: (businessId: string, handoffId: string) =>
    `/admin/businesses/${businessId}/stripe-payout-card/handoff/${handoffId}`,
  // AUTH
  auth: {
    login: `${ROOTS.AUTH}/login`,
    loginCompanion: `${ROOTS.AUTH}/login-companion`,
    loginPointPay: `${ROOTS.AUTH}/p/login`,
    v3DeviceLogin: `${ROOTS.AUTH}/v3-device-login`,
    // register: `${ROOTS.AUTH}/jwt/register`,
    // verify: `${ROOTS.AUTH}/jwt/verify`,
    // forgotPassword: `${ROOTS.AUTH}/jwt/forgot-password`,

    // firebase: {
    //   // login: `${ROOTS.AUTH}/firebase/login`,
    //   verify: `${ROOTS.AUTH}/firebase/verify`,
    //   // register: `${ROOTS.AUTH}/firebase/register`,
    //   forgotPassword: `${ROOTS.AUTH}/firebase/forgot-password`,
    // },
  },
  system: {
    updateVersion: `${ROOTS.SYSTEM}/update-version`,
    contactSupport: `${ROOTS.SYSTEM}/contact-support`,
    offline: `${ROOTS.SYSTEM}/offline`,
    permissions: `${ROOTS.SYSTEM}/permissions`,
  },
  companion: {
    root: `${ROOTS.COMPANION}`,
    menu: `${ROOTS.COMPANION}/menu`,
    selfCheckout: `${ROOTS.COMPANION}/self-checkout`,
    customerFacing: `${ROOTS.COMPANION}/customer-facing`,
    digitalSignage: `${ROOTS.COMPANION}/digital-signage`,
    notSetup: `${ROOTS.COMPANION}/not-setup`,
  },
  operationalApps: {
    root: ROOTS.OPERATIONAL_APPS,
    kds: `${ROOTS.OPERATIONAL_APPS}/kds`,
    selfCheckout: `${ROOTS.OPERATIONAL_APPS}/self-checkout`,
  },
  // DASHBOARD
  // This page is connected to src/routes/sections/dashboard.tsx
  // If you change the path here, you must also change the path in the dashboard.tsx file
  dashboard: {
    root: ROOTS.DASHBOARD,
    appHub: `${ROOTS.DASHBOARD}/app-hub`,
    apps: `${ROOTS.DASHBOARD}/dashboard`,
    transactions: {
      root: `${ROOTS.DASHBOARD}/transactions`,
      // history: `${ROOTS.DASHBOARD}/transactions/history`,
      details: (id: string) => `${ROOTS.DASHBOARD}/transactions/details/${id}`,
    },
    reports: {
      root: `${ROOTS.DASHBOARD}/reports`,
      tab: (tab?: string) => `${ROOTS.DASHBOARD}/reports/${tab}`,
    },
    inventory: {
      root: `${ROOTS.DASHBOARD}/inventory`,
      menu: `${ROOTS.DASHBOARD}/inventory/menu`,
    },
    customers: {
      root: `${ROOTS.DASHBOARD}/customers`,
      details: (id: string) => `${ROOTS.DASHBOARD}/customers/details/${id}`,
    },
    settings: {
      root: `${ROOTS.DASHBOARD}/settings`,
    },
    admin: {
      root: `${ROOTS.DASHBOARD}/admin`,
      tab: (tab?: string) => (tab ? `${ROOTS.DASHBOARD}/admin/${tab}` : `${ROOTS.DASHBOARD}/admin`),
      singleUser: (id: string) => `${ROOTS.DASHBOARD}/admin/user/${id}`,
      singleBusiness: (id: string) => `${ROOTS.DASHBOARD}/admin/business/${id}`,
      businessesDetail: (id: string) => `${ROOTS.DASHBOARD}/admin/businesses/${id}`,
      reconciliationRun: (runId: string) =>
        `${ROOTS.DASHBOARD}/admin/reconciliation/runs/${encodeURIComponent(runId)}`,
    },
    platformOps: {
      root: `${ROOTS.DASHBOARD}/platform-ops`,
      businesses: `${ROOTS.DASHBOARD}/platform-ops/businesses`,
      businessDetail: (businessId: string) =>
        `${ROOTS.DASHBOARD}/platform-ops/businesses/${encodeURIComponent(businessId)}`,
      equipment: `${ROOTS.DASHBOARD}/platform-ops/equipment`,
      issues: `${ROOTS.DASHBOARD}/platform-ops/issues`,
      boards: `${ROOTS.DASHBOARD}/platform-ops/boards`,
      board: (boardId: string) =>
        `${ROOTS.DASHBOARD}/platform-ops/boards/${encodeURIComponent(boardId)}`,
      deployments: `${ROOTS.DASHBOARD}/platform-ops/deployments`,
      deployment: (deploymentId: string) =>
        `${ROOTS.DASHBOARD}/platform-ops/deployments/${encodeURIComponent(deploymentId)}`,
    },
    ebtTransactions: {
      root: `${ROOTS.DASHBOARD}/ebt-transactions`,
    },
    v3: {
      root: `${ROOTS.DASHBOARD}/v3`,
      // Compatibility URL. The router redirects this route to POS V4.
      pos: `${ROOTS.DASHBOARD}/v3/pos`,
      selfCheckout: `${ROOTS.DASHBOARD}/v3/restaurant-self-checkout`,
    },
    v4: {
      root: `${ROOTS.DASHBOARD}/v4`,
      pos: `${ROOTS.DASHBOARD}/v4/pos`,
      payment: `${ROOTS.DASHBOARD}/v4/payment`,
      timeClock: `${ROOTS.DASHBOARD}/v4/time-clock`,
      transactions: `${ROOTS.DASHBOARD}/v4/transactions`,
      inventory: `${ROOTS.DASHBOARD}/v4/inventory`,
      restaurantPos: `${ROOTS.DASHBOARD}/v4/restaurant-pos`,
      // Full-page Orders Hub. Nested under the Restaurant POS so both pages share
      // one orders controller and the cart-exit guard treats them as one flow.
      restaurantOrdersHub: `${ROOTS.DASHBOARD}/v4/restaurant-pos/orders-hub`,
      mobileInventory: `${ROOTS.DASHBOARD}/v4/mobile-inventory`,
      customers: {
        root: `${ROOTS.DASHBOARD}/v4/customers`,
        details: (id: string) => `${ROOTS.DASHBOARD}/v4/customers/${id}`,
      },
    },
    public: {
      receipt: (id: string) => `${ROOTS.PUBLIC}/receipt/${id}`,
      menu: `${ROOTS.PUBLIC}/menu`,
    },

    // three: `${ROOTS.DASHBOARD}/three`,
    // group: {
    //   root: `${ROOTS.DASHBOARD}/group`,
    //   five: `${ROOTS.DASHBOARD}/group/five`,
    //   six: `${ROOTS.DASHBOARD}/group/six`,
    // },
  },

  pointPay: {
    root: `${ROOTS.ONLINE_PAYMENT}`,
    dashboard: `${ROOTS.ONLINE_PAYMENT}`,
    settings: `${ROOTS.ONLINE_PAYMENT}/settings`,
    transactions: `${ROOTS.ONLINE_PAYMENT}/transactions`,
    transaction: buildPointPaySalePath,
    customers: `${ROOTS.ONLINE_PAYMENT}/customers`,
    paymentLinks: `${ROOTS.ONLINE_PAYMENT}/payment-links`,
    /**
     * Payforms (Public Experiences, merchant self-service). `customers` here is
     * the connected-account customer directory — distinct from the SQL
     * `/online-payment/customers` page above, which is untouched.
     */
    payforms: {
      root: `${ROOTS.ONLINE_PAYMENT}/payforms`,
      new: `${ROOTS.ONLINE_PAYMENT}/payforms/new`,
      detail: (experienceId: string) =>
        `${ROOTS.ONLINE_PAYMENT}/payforms/${encodeURIComponent(experienceId)}`,
      submissions: (experienceId: string) =>
        `${ROOTS.ONLINE_PAYMENT}/payforms/${encodeURIComponent(experienceId)}/submissions`,
      customers: `${ROOTS.ONLINE_PAYMENT}/payforms/customers`,
      customer: (customerId: string) =>
        `${ROOTS.ONLINE_PAYMENT}/payforms/customers/${encodeURIComponent(customerId)}`,
    },
  },
};
